import { Loader2, ExternalLink, Building2, Users, Kanban, CheckSquare, Receipt, FileText, Wrench, Package } from "lucide-react"
import { TOOL_LABELS } from "@/lib/ai-types"
import type { RichToolCall } from "@/lib/ag-ui-types"
import { Link } from "react-router-dom"

const TOOL_ENTITY_MAP: Record<string, { icon: React.ElementType; color: string }> = {
  get_company: { icon: Building2, color: "#3b82f6" },
  get_contact: { icon: Users, color: "#10b981" },
  get_deal: { icon: Kanban, color: "#8b5cf6" },
  get_task: { icon: CheckSquare, color: "#f59e0b" },
  get_invoice: { icon: Receipt, color: "#f43f5e" },
  get_quote: { icon: FileText, color: "#06b6d4" },
  get_ticket: { icon: Wrench, color: "#f97316" },
  get_product: { icon: Package, color: "#6366f1" },
}

function parseRecord(result: string | null): Record<string, unknown> | null {
  if (!result) return null
  try {
    const parsed = JSON.parse(result)
    return parsed.data || parsed
  } catch {
    return null
  }
}

const KEY_FIELDS = ["name", "title", "email", "phone", "status", "value", "amount", "total", "phase"]

export function RecordDetailCard({ toolCall }: { toolCall: RichToolCall }) {
  const label = TOOL_LABELS[toolCall.name] || toolCall.name
  const isDone = toolCall.status === "done"
  const record = parseRecord(toolCall.result)
  const config = TOOL_ENTITY_MAP[toolCall.name] || { icon: Building2, color: "#9ca3af" }
  const Icon = config.icon

  const displayFields = record
    ? KEY_FIELDS.filter((k) => record[k] != null).slice(0, 3)
    : []

  const url = record?.url as string | undefined

  return (
    <div
      className="my-2 overflow-hidden rounded-lg"
      style={{
        border: "1px solid var(--border)",
        borderTop: `2px solid ${config.color}`,
        background: "var(--bg-surface)",
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        {isDone ? (
          <Icon size={14} style={{ color: "var(--text-muted)" }} />
        ) : (
          <Loader2 size={14} className="animate-spin" style={{ color: "var(--primary)" }} />
        )}
        <span className="flex-1 text-xs font-medium" style={{ color: "var(--text-primary)" }}>
          {isDone && record ? (record.name || record.title || label) as string : label}
        </span>
        {isDone && url && (
          <Link to={url as string} style={{ color: "var(--primary)" }} className="hover:opacity-80">
            <ExternalLink size={12} />
          </Link>
        )}
      </div>

      {isDone && record && displayFields.length > 0 && (
        <div className="px-3 py-2" style={{ borderTop: "1px solid var(--border)" }}>
          {displayFields.map((key) => (
            <div key={key} className="flex items-center justify-between text-xs">
              <span className="capitalize" style={{ color: "var(--text-muted)" }}>{key}</span>
              <span className="font-medium" style={{ color: "var(--text-primary)" }}>{String(record[key])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
